import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from '../../hooks/useTranslation';

// Google logo SVG
const GoogleLogo = () => (
  <svg width="18" height="18" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48">
    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
    <path fill="none" d="M0 0h48v48H0z"/>
  </svg>
);

// Microsoft logo SVG
const MicrosoftLogo = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg">
    <rect x="0" y="0" width="7.5" height="7.5" fill="#F25022" />
    <rect x="8.5" y="0" width="7.5" height="7.5" fill="#7FBA00" />
    <rect x="0" y="8.5" width="7.5" height="7.5" fill="#00A4EF" />
    <rect x="8.5" y="8.5" width="7.5" height="7.5" fill="#FFB900" />
  </svg>
);

function SignupModal({ isOpen, onClose }) {
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [showPasswordRequirements, setShowPasswordRequirements] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [termsHighlight, setTermsHighlight] = useState(false);
  
  const { 
    signup, 
    error, 
    setError, 
    googleSignInWithPopup, 
    googleSignIn, 
    microsoftSignInWithPopup, 
    microsoftSignIn,
    getFormattedErrorMessage,
    redirectToApp
  } = useAuth();
  const t = useTranslation();
  
  // Signup form refs
  const signupNameRef = useRef();
  const signupEmailRef = useRef();
  const signupPasswordRef = useRef();
  const signupConfirmPasswordRef = useRef();
  
  // Reset states when modal is opened
  useEffect(() => {
    if (isOpen) {
      setShowPasswordRequirements(false);
      setMessage({ type: '', text: '' });
      setError('');
    }
  }, [isOpen]);
  
  // Handle signup form submission
  async function handleSignup(e) {
    e.preventDefault();
    
    // Clear previous messages
    setMessage({ type: '', text: '' });
    setError('');
    
    if (!agreedToTerms) {
      setMessage({ type: 'error', text: 'You must agree to the Terms and Conditions and Privacy Policy.' });
      setTermsHighlight(true);
      return;
    }
    setTermsHighlight(false);
    
    const name = signupNameRef.current.value;
    const email = signupEmailRef.current.value;
    const password = signupPasswordRef.current.value;
    const confirmPassword = signupConfirmPasswordRef.current.value;
    
    // Validate passwords match
    if (password !== confirmPassword) {
      return setMessage({ 
        type: 'error', 
        text: 'Passwords do not match. Please make sure both passwords are identical.'
      });
    }
    
    // Validate password requirements
    const passwordErrors = validatePassword(password);
    if (passwordErrors.length > 0) {
      setShowPasswordRequirements(true);
      return setMessage({
        type: 'error',
        text: 'Your password does not meet the requirements below. Please review and try again.'
      });
    }
    
    try {
      setIsAuthenticating(true);
      // Signup successful - returns { user, customToken }
      const { customToken } = await signup(email, password, name);
      
      // Pass the custom token to redirectToApp
      redirectToApp(customToken);
    } catch (error) {
      console.error("Signup error:", error);
      
      // Use the destructured function directly
      const errorMessage = getFormattedErrorMessage(error);
      setMessage({ type: 'error', text: errorMessage });
      
      // Show requirements on weak password specifically
      if (error.code === 'auth/weak-password') {
        setShowPasswordRequirements(true);
      }
    } finally {
      setIsAuthenticating(false);
    }
  }
  
  // Function to validate password
  function validatePassword(password) {
    const errors = [];
    
    if (password.length < 10) {
      errors.push(t('errors.passwordMinLength'));
    }
    
    if (!/[A-Z]/.test(password)) {
      errors.push(t('errors.passwordUppercase'));
    }
    
    if (!/[a-z]/.test(password)) {
      errors.push(t('errors.passwordLowercase'));
    }
    
    if (!/[0-9]/.test(password)) {
      errors.push(t('errors.passwordNumber'));
    }
    
    if (!/[^A-Za-z0-9]/.test(password)) {
      errors.push(t('errors.passwordSpecial'));
    }
    
    return errors;
  }
  
  // Reset password requirements visibility when user starts typing a new password
  const handlePasswordChange = () => {
    if (showPasswordRequirements && message.type === 'error') {
      setMessage({ type: '', text: '' });
    }
  };
  
  // Handle password field focus after an error
  const handlePasswordFocus = () => {
    // If there was a previous password error, show requirements on focus
    if (message.type === 'error' && 
        (message.text.includes('password') || message.text.includes('Password'))) {
      setShowPasswordRequirements(true);
    }
  };
  
  // Handle Google sign-in with popup
  async function handleGoogleSignIn() {
    setMessage({ type: '', text: '' });
    setError('');
    
    try {
      setIsAuthenticating(true);
      console.log('Initiating Google sign-in with popup');
      // Try popup sign-in first - returns { user, customToken }
      const { customToken } = await googleSignInWithPopup();
      console.log('Google sign-in with popup completed, got custom token.');
      
      // Pass the custom token to redirectToApp
      redirectToApp(customToken);
      
    } catch (popupError) {
      // If popup fails, fall back to redirect or show formatted error
      if (popupError.code === 'auth/popup-blocked' || 
          popupError.code === 'auth/cancelled-popup-request' ||
          popupError.code === 'auth/popup-closed-by-user') {
        console.log('Popup failed/closed, trying redirect method', popupError.code);
        try {
          await googleSignIn(); // This is the redirect method, no token exchange here
          console.log('Google sign-in redirect initiated');
          // Redirect happens automatically, no need to call redirectToApp
        } catch (redirectError) {
          console.error("Google redirect initiation error:", redirectError);
          // Show error from trying to initiate redirect
          const errorMessage = getFormattedErrorMessage(redirectError);
          setMessage({ type: 'error', text: errorMessage });
        }
      } else {
        // Handle other popup errors
        console.error("Google sign-in popup error:", popupError);
        const errorMessage = getFormattedErrorMessage(popupError);
        setMessage({ type: 'error', text: errorMessage });
      }
    } finally {
      setIsAuthenticating(false);
    }
  }
  
  // Handle Microsoft sign-in with popup
  async function handleMicrosoftSignIn() {
    setMessage({ type: '', text: '' });
    setError('');
    
    try {
      setIsAuthenticating(true);
      console.log('Initiating Microsoft sign-in with popup');
      // Try popup sign-in first - returns { user, customToken }
      const { customToken } = await microsoftSignInWithPopup();
      console.log('Microsoft sign-in with popup completed, got custom token.');
      
      // Pass the custom token to redirectToApp
      redirectToApp(customToken);

    } catch (popupError) {
      // If popup fails, fall back to redirect
      if (popupError.code === 'auth/popup-blocked' || 
          popupError.code === 'auth/cancelled-popup-request' ||
          popupError.code === 'auth/popup-closed-by-user') {
        console.log('Popup failed/closed, trying redirect method', popupError.code);
        try {
          await microsoftSignIn(); // This is the redirect method
          console.log('Microsoft sign-in redirect initiated');
          // Redirect happens automatically
        } catch (redirectError) {
          console.error("Microsoft redirect initiation error:", redirectError);
          // Show error from trying to initiate redirect
          const errorMessage = getFormattedErrorMessage(redirectError);
          setMessage({ type: 'error', text: errorMessage });
        }
      } else {
         // Handle other popup errors
        console.error("Microsoft sign-in popup error:", popupError);
        const errorMessage = getFormattedErrorMessage(popupError);
        setMessage({ type: 'error', text: errorMessage });
      }
    } finally {
      setIsAuthenticating(false);
    }
  }

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black bg-opacity-50" onClick={onClose}></div>
      
      {/* Modal Content */}
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4 relative z-10 overflow-hidden">
        {/* Close button */}
        <button 
          className="absolute top-4 right-4 text-gray-500 hover:text-gray-800"
          onClick={onClose}
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
        
        <div className="p-6">
          <div className="mb-6 text-center">
            <h2 className="text-2xl font-bold text-gray-800 mb-2">{t('auth.createAccount')}</h2>
            <p className="text-gray-600">{t('auth.signup')}</p>
          </div>
          
          {/* Display error or success message */}
          {error && (
            <div className="mb-4 p-4 bg-red-50 border-l-4 border-red-400 text-red-700 rounded-md flex items-start">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 mt-0.5 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              <span>{error}</span>
            </div>
          )}
          
          {message.text && (
            <div className={`mb-4 p-4 rounded-md flex items-start ${
              message.type === 'error' 
                ? 'bg-red-50 border-l-4 border-red-400 text-red-700' 
                : message.type === 'warning' 
                  ? 'bg-yellow-50 border-l-4 border-yellow-400 text-yellow-700' 
                  : 'bg-green-50 border-l-4 border-green-400 text-green-700'
            }`}>
              {message.type === 'error' && (
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 mt-0.5 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              )}
              {message.type === 'warning' && (
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 mt-0.5 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              )}
              {message.type === 'success' && (
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 mt-0.5 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              )}
              <span>{message.text}</span>
            </div>
          )}
          
          <form className="space-y-4" onSubmit={handleSignup}>
            <div className="space-y-1">
              <label htmlFor="signup-name" className="block text-sm font-medium text-gray-700">
                {t('auth.name')}
              </label>
              <input
                id="signup-name"
                type="text"
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400
                bg-white text-gray-900 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                placeholder={t('auth.enterName')}
                required
                ref={signupNameRef}
              />
            </div>
            
            <div className="space-y-1">
              <label htmlFor="signup-email" className="block text-sm font-medium text-gray-700">
                {t('auth.email')}
              </label>
              <input
                id="signup-email"
                type="email"
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400
                bg-white text-gray-900 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                placeholder={t('auth.enterEmail')}
                required
                ref={signupEmailRef}
              />
            </div>
            
            <div className="space-y-1">
              <label htmlFor="signup-password" className="block text-sm font-medium text-gray-700">
                {t('auth.password')}
              </label>
              <input
                id="signup-password"
                type="password"
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400
                bg-white text-gray-900 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                placeholder={t('auth.enterPassword')}
                required
                ref={signupPasswordRef}
                onChange={handlePasswordChange}
                onFocus={handlePasswordFocus}
              />
              {showPasswordRequirements && (
                <div className="mt-1 text-xs text-gray-500">
                  <p className="mb-1 font-medium">Password requirements:</p>
                  <ul className="list-disc pl-5 space-y-0.5">
                    <li>At least 10 characters</li>
                    <li>At least one uppercase letter</li>
                    <li>At least one lowercase letter</li>
                    <li>At least one number</li>
                    <li>At least one special character</li>
                  </ul>
                </div>
              )}
            </div>
            
            <div className="space-y-1">
              <label htmlFor="signup-confirm-password" className="block text-sm font-medium text-gray-700">
                {t('auth.confirmPassword')}
              </label>
              <input
                id="signup-confirm-password"
                type="password"
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400
                bg-white text-gray-900 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                placeholder={t('auth.confirmYourPassword')}
                required
                ref={signupConfirmPasswordRef}
              />
            </div>
            
            <div className="flex items-start mt-2 mb-2">
              <input
                id="agree-terms"
                type="checkbox"
                className={`h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 mt-1 transition-colors duration-200 ${termsHighlight && !agreedToTerms ? 'border-red-500 ring-2 ring-red-400' : ''}`}
                checked={agreedToTerms}
                onChange={e => { setAgreedToTerms(e.target.checked); if (e.target.checked) setTermsHighlight(false); }}
                required
              />
              <label htmlFor="agree-terms" className="ml-2 text-sm text-gray-700 select-none">
                I agree to the{' '}
                <a href="/terms" target="_blank" rel="noopener noreferrer" className="underline text-blue-600 hover:text-blue-800">Terms and Conditions</a>{' '}and{' '}
                <a href="/privacy" target="_blank" rel="noopener noreferrer" className="underline text-blue-600 hover:text-blue-800">Privacy Policy</a>
              </label>
            </div>
            
            <button 
              type="submit" 
              className="w-full py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700
              focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={isAuthenticating}
            >
              {isAuthenticating ? t('common.processing') : t('auth.createAccount')}
            </button>
          </form>

          <div className="relative flex items-center justify-center my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300"></div>
            </div>
            <div className="relative px-4 bg-white text-sm text-gray-500">
              {t('auth.or')}
            </div>
          </div>
          
          <div className="space-y-3 flex flex-col items-center">
            {/* Google Sign-in Button */}
            <button 
              className="w-64 flex items-center justify-center h-[41px] px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium transition-colors duration-200
              bg-white text-[#1F1F1F] hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={handleGoogleSignIn} 
              disabled={isAuthenticating}
            >
              {isAuthenticating ? (
                t('common.loading')
              ) : (
                <div className="flex items-center w-full justify-center">
                  <span className="mr-2.5">
                    <GoogleLogo />
                  </span>
                  <span>{t('auth.continueWithGoogle')}</span>
                </div>
              )}
            </button>
            
            {/* Microsoft Sign-in Button */}
            <button 
              className="w-64 flex items-center justify-center h-[41px] px-4 border rounded-sm shadow-sm font-semibold text-sm transition-colors duration-200
              bg-white text-[#5E5E5E] hover:bg-gray-50 border-[#8C8C8C] disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={handleMicrosoftSignIn} 
              disabled={isAuthenticating}
            >
              {isAuthenticating ? (
                t('common.loading')
              ) : (
                <div className="flex items-center w-full justify-center">
                  <span className="flex-shrink-0 pl-[12px]">
                    <MicrosoftLogo />
                  </span>
                  <span className="pl-[12px] pr-[12px]">{t('auth.continueWithMicrosoft')}</span>
                </div>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SignupModal; 